import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import AddNewFolderName from './AddNewFolderName';
import SelectedFolder from './SelectedFolder';
import LocationGalleries from './LocationGalleries';
import { createGuid, emptyFunction, showToast } from '../../../utils';
import DeleteImagePopup from './DeleteImagePopup';
import { endPoints } from '../../../utils/endpoints';
import { makeDeleteCall } from '../../../effects/requests';
import { ApiResponseType } from '../../../types';

interface Folder {
    id: string;
    name: string;
    photos: string[];
    isExistingFolder: boolean
}

type View = 'galleries' | 'folder' | 'new-folder';

interface Props {
    folderEntries: Array<any>;
    setFolderEntries: (data: any) => void;
    onRefresh: () => void;
    setView: (type: any) => void;
    view: View
}

const StepAddPhotos = forwardRef(({ onRefresh, setView, view, folderEntries, setFolderEntries }: Props, ref: any) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activeFolder, setActiveFolder] = useState<any>(null);
    const [selectedImageId, setSelectedImageId] = useState<any>(null);
    const [newFolderName, setNewFolderName] = useState('');

    // const handleUpload = (files: FileList | null) => {
    //     if (!files || !activeFolder) return;

    //     const newPhotos = Array.from(files).map((file) => ({
    //         id: "",
    //         name: file?.name || 'Test',
    //         image: URL.createObjectURL(file),
    //         file: file
    //     }));

    //     const updatedFolder = {
    //         ...activeFolder,
    //         photos: [...activeFolder.photos, ...newPhotos],
    //     };

    //     // Update active folder
    //     setActiveFolder(updatedFolder);

    //     // Update folder list
    //     setFolderEntries((prev: any) => prev.map((folder: any) => folder.id === updatedFolder.id ? updatedFolder : folder));

    //     if (fileInputRef.current) {
    //         fileInputRef.current.value = "";
    //     }
    // };

    const handleUpload = (files: FileList | null) => {
        if (!files || !activeFolder) return;

        const newPhotos = [];

        for (const file of Array.from(files)) {
            const isDuplicate = activeFolder.photos.some(
                (photo: any) =>
                    photo.file &&
                    photo.file.name === file.name &&
                    photo.file.size === file.size &&
                    photo.file.lastModified === file.lastModified
            );

            if (isDuplicate) {
                showToast(`${file.name} is already added.`, 'error');
                continue; // Skip this file
            }

            newPhotos.push({
                id: "",
                name: file.name,
                image: URL.createObjectURL(file),
                file,
                type: file?.type || 'image'
            });
        }

        if (newPhotos.length === 0) {
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const updatedFolder = {
            ...activeFolder,
            photos: [...activeFolder.photos, ...newPhotos],
        };

        setActiveFolder(updatedFolder);

        setFolderEntries((prev: any) =>
            prev.map((folder: any) =>
                folder.id === updatedFolder.id ? updatedFolder : folder
            )
        );

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleRemovePhoto = () => {
        const url = `${endPoints.LOCATION_FOLDER_IMAGE}${selectedImageId}/`
        makeDeleteCall(url).then((res: ApiResponseType<any>) => {
            if (res?.status?.code === 200) {
                showToast(res?.status?.message, "success")
                setSelectedImageId(null)
                onRefresh();
            }
        }).catch(emptyFunction)
    };

    const handleCreateFolder = () => {
        if (!newFolderName?.trim()) {
            showToast('Enter folder name.', 'error')
            return;
        }
        const newFolder: Folder = { id: createGuid(), name: newFolderName?.trim(), photos: [], isExistingFolder: false };
        setFolderEntries((prev: any) => [...prev, newFolder]);
        setActiveFolder(null)
        setView('galleries');
        setNewFolderName('');
    };

    const onImportFolder = (data: any) => {
        const tempFolders = [...folderEntries, ...data];
        setFolderEntries(tempFolders)
    }

    useImperativeHandle(ref, () => ({
        handleCreateFolder: handleCreateFolder
    }))

    return (
        <>
            {/* Galleries view */}
            {view === 'galleries' &&
                <LocationGalleries
                    folders={folderEntries}
                    onCreateFolder={() => setView('new-folder')}
                    onImportFolder={onImportFolder}
                    setActiveFolder={(folder) => {
                        setActiveFolder(folder);
                        setView('folder')
                    }}
                    onDeleteFolder={(id: any) => {
                        const tempFolders = [...folderEntries];
                        setFolderEntries(tempFolders?.filter(folder => folder?.id !== id));
                    }}
                />

            }
            {/* New folder view */}
            {view == 'new-folder' &&
                <AddNewFolderName
                    newFolderName={newFolderName}
                    setNewFolderName={setNewFolderName}
                    setView={setView}
                />
            }
            {/* Folder contents view */}
            {view === 'folder' &&
                <SelectedFolder
                    activeFolder={activeFolder}
                    handleRemovePhoto={(photo: any, photoIdx: number) => {
                        if (photo?.id) {
                            setSelectedImageId(photo?.id);
                            return;
                        }

                        const updatedPhotos = activeFolder.photos.filter((_: any, index: any) => index !== photoIdx);

                        const updatedFolder = {
                            ...activeFolder,
                            photos: updatedPhotos,
                        };

                        // Update active folder
                        setActiveFolder(updatedFolder);

                        // Update folders list
                        setFolderEntries((prev: any) =>
                            prev.map((folder: any) =>
                                folder?.id === updatedFolder?.id ? updatedFolder : folder
                            )
                        );
                    }}
                    handleUpload={handleUpload}
                    ref={fileInputRef}
                    setView={setView}
                    makeThumbnail={(idx: number) => {
                        const updatedPhotos = activeFolder?.photos?.map(
                            (photo: any, index: number) => ({
                                ...photo,
                                thumbnail: index === idx ? true : false,
                            })
                        );

                        const updatedFolder = {
                            ...activeFolder,
                            photos: updatedPhotos,
                        };

                        setActiveFolder(updatedFolder);

                        setFolderEntries((prev: any) =>
                            prev.map((folder: any) =>
                                folder?.id === updatedFolder?.id
                                    ? updatedFolder
                                    : folder
                            )
                        );
                    }}
                />
            }
            {selectedImageId &&
                <DeleteImagePopup
                    onClose={() => setSelectedImageId(null)}
                    onDelete={handleRemovePhoto}
                />
            }
        </>
    )
});

export default StepAddPhotos;